import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AufgabenOverview.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Aufgabe {
  aufgabe_id: number;
  entitaet_typ: string;
  entitaet_id: number;
  titel: string;
  beschreibung: string | null;
  faellig_am: string | null;
  zugewiesen_an: number | null;
  status: 'Offen' | 'In Arbeit' | 'Erledigt';
  erstellt_von: number | null;
  erstellt_am: string | null;
  erledigt_am: string | null;
}

interface AufgabenOverviewProps {
  authToken: string;
}

function formatDatum(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function getFaelligKlasse(faellig_am: string | null, status: string): string {
  if (!faellig_am || status === 'Erledigt') return '';
  const faellig = new Date(faellig_am);
  const jetzt = new Date();
  const heute = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  const faelligTag = new Date(faellig.getFullYear(), faellig.getMonth(), faellig.getDate());
  if (faelligTag < heute) return 'ueberfaellig';
  if (faelligTag.getTime() === heute.getTime()) return 'heute';
  return '';
}

const STATUS_COLOR: Record<string, string> = {
  'Offen': 'var(--gold)',
  'In Arbeit': 'var(--green-900)',
  'Erledigt': 'var(--green-500)',
};

const AufgabenOverview: React.FC<AufgabenOverviewProps> = ({ authToken }) => {
  const [aufgaben, setAufgaben] = useState<Aufgabe[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'alle' | 'Offen' | 'In Arbeit' | 'Erledigt'>('Offen');

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = `${API_URL}/api/aufgaben/meine`;
      const res = await axios.get(url, { headers });
      setAufgaben(res.data);
    } catch {
      setError('Aufgaben konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (aufgabeId: number, status: string) => {
    try {
      await axios.put(
        `${API_URL}/api/aufgaben/${aufgabeId}/status`,
        { status },
        { headers }
      );
      load();
    } catch {
      setError('Status konnte nicht aktualisiert werden.');
    }
  };

  const gefilterteAufgaben = statusFilter === 'alle'
    ? aufgaben
    : aufgaben.filter(a => a.status === statusFilter);

  const offen = aufgaben.filter(a => a.status === 'Offen').length;
  const inArbeit = aufgaben.filter(a => a.status === 'In Arbeit').length;
  const erledigt = aufgaben.filter(a => a.status === 'Erledigt').length;

  return (
    <div className="ao-container">
      <div className="ao-header">
        <h2 className="ao-title">Aufgaben</h2>
        <button className="ao-refresh-btn" onClick={load} disabled={loading}>
          {loading ? '⏳' : '↻'} Aktualisieren
        </button>
      </div>

      {/* Statistik-Kacheln */}
      <div className="ao-stats">
        <div className="ao-stat-card offen" onClick={() => setStatusFilter('Offen')}>
          <span className="ao-stat-zahl">{offen}</span>
          <span className="ao-stat-label">Offen</span>
        </div>
        <div className="ao-stat-card in-arbeit" onClick={() => setStatusFilter('In Arbeit')}>
          <span className="ao-stat-zahl">{inArbeit}</span>
          <span className="ao-stat-label">In Arbeit</span>
        </div>
        <div className="ao-stat-card erledigt" onClick={() => setStatusFilter('Erledigt')}>
          <span className="ao-stat-zahl">{erledigt}</span>
          <span className="ao-stat-label">Erledigt</span>
        </div>
      </div>

      {/* Filter-Zeile */}
      <div className="ao-filter-row">
        <div className="ao-filter-group">
          {(['alle', 'Offen', 'In Arbeit', 'Erledigt'] as const).map(s => (
            <button
              key={s}
              className={`ao-filter-btn ${statusFilter === s ? 'active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'alle' ? 'Alle' : s}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="ao-error">{error}</div>}

      {loading ? (
        <div className="ao-loading">Aufgaben werden geladen…</div>
      ) : gefilterteAufgaben.length === 0 ? (
        <div className="ao-empty">
          
          <p>Keine Aufgaben für diesen Filter vorhanden.</p>
        </div>
      ) : (
        <div className="ao-list">
          {gefilterteAufgaben.map(a => {
            const faelligKlasse = getFaelligKlasse(a.faellig_am, a.status);
            return (
              <div
                key={a.aufgabe_id}
                className={`ao-item ${a.status === 'Erledigt' ? 'erledigt' : ''} ${faelligKlasse}`}
              >
                <div className="ao-item-header">
                  <span className="ao-titel">{a.titel}</span>
                  <span
                    className="ao-status-badge"
                    style={{ background: STATUS_COLOR[a.status] || 'var(--ink-2)' }}
                  >
                    {a.status}
                  </span>
                </div>
                {a.beschreibung && (
                  <div className="ao-beschreibung">{a.beschreibung}</div>
                )}
                <div className="ao-item-footer">
                  <div className="ao-meta">
                    <span className="ao-entitaet">
                      {a.entitaet_typ} #{a.entitaet_id}
                    </span>
                    {a.faellig_am && (
                      <span className={`ao-faellig ${faelligKlasse}`}>
                        {faelligKlasse === 'ueberfaellig' ? 'Überfällig: ' : faelligKlasse === 'heute' ? 'Heute: ' : 'Fällig: '}
                        {formatDatum(a.faellig_am)}
                      </span>
                    )}
                  </div>
                  <div className="ao-actions">
                    {a.status !== 'Offen' && (
                      <button className="ao-btn small" onClick={() => updateStatus(a.aufgabe_id, 'Offen')}>
                        Offen
                      </button>
                    )}
                    {a.status !== 'In Arbeit' && (
                      <button className="ao-btn small secondary" onClick={() => updateStatus(a.aufgabe_id, 'In Arbeit')}>
                        In Arbeit
                      </button>
                    )}
                    {a.status !== 'Erledigt' && (
                      <button className="ao-btn small success" onClick={() => updateStatus(a.aufgabe_id, 'Erledigt')}>
                        ✓ Erledigt
                      </button>
                    )}
                  </div>
                </div>
                {a.erledigt_am && (
                  <div className="ao-erledigt-am">Erledigt am: {formatDatum(a.erledigt_am)}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AufgabenOverview;
